(function () {
  class SwymcsSflContainer extends HTMLElement {
    // Subscribes to SFL updates and kicks off the first fetch when the drawer mounts this element.
    connectedCallback() {
      this._isConnected = true;
      this.listEl = this.querySelector('#swymcs-sfl-list');
      this._addEventListeners();
      SwymcsSflContainer.updateSflCache();
    }

    // Detaches listeners so a removed element doesn't keep reacting to drawer/SFL events.
    disconnectedCallback() {
      this._isConnected = false;
      this._removeEventListeners();
    }

    // Wires up both event sources this element reacts to: SFL updates and the drawer's open transition.
    _addEventListeners() {
      this._addSflFetchedListener();
      this._addCartDrawerTransitionListener();
    }

    // Undoes _addEventListeners.
    _removeEventListeners() {
      this._removeSflFetchedListener();
      this._removeCartDrawerTransitionListener();
    }

    // Listens for the shared SFL context refreshing, so this container knows when to re-render.
    _addSflFetchedListener() {
      var context = window.SwymStorefrontLayoutContext;
      this._sflFetchedEvent = (context && context.CustomEvents && context.CustomEvents.SavedForLaterFetched) || 'SwymSavedForLaterFetched';
      this._onSflFetched = this._onSflFetched.bind(this);
      document.addEventListener(this._sflFetchedEvent, this._onSflFetched);
    }

    // Stops listening for SFL updates.
    _removeSflFetchedListener() {
      if (this._sflFetchedEvent && this._onSflFetched) {
        document.removeEventListener(this._sflFetchedEvent, this._onSflFetched);
      }
    }

    // Listens for the cart drawer's open transition, so focus trap can be re-widened right after it.
    _addCartDrawerTransitionListener() {
      var self = this;
      var cartDrawer = document.querySelector('cart-drawer');
      var drawerInner = cartDrawer && cartDrawer.querySelector('.drawer__inner');
      if (!drawerInner) return;

      // Deferred one tick so this runs after CartDrawer's own trapFocus reset on the same transitionend.
      this._drawerInner = drawerInner;
      this._onDrawerTransitionEnd = function (event) {
        if (event.target !== drawerInner) return;
        if (!cartDrawer.classList.contains('active')) return;
        setTimeout(function () {
          self._updateFocusTrap();
        }, 0);
      };
      drawerInner.addEventListener('transitionend', this._onDrawerTransitionEnd);
    }

    // Stops listening for the drawer transition, and clears the reference so it can't be reused stale.
    _removeCartDrawerTransitionListener() {
      if (this._drawerInner && this._onDrawerTransitionEnd) {
        this._drawerInner.removeEventListener('transitionend', this._onDrawerTransitionEnd);
      }
      this._drawerInner = null;
      this._onDrawerTransitionEnd = null;
    }

    // Re-renders from the shared context once a fresh SFL fetch lands, and releases the write-guard flag.
    _onSflFetched() {
      window._acqTrustedListWrite = false;
      if (!this._isConnected) return;
      var context = window.SwymStorefrontLayoutContext;
      this.render((context && context.sflListItems) || []);
    }

    // Rebuilds the item list markup and rewires each item's Move to Cart / Remove buttons.
    render(items) {
      if (!this.listEl) return;
      if (!items.length) {
        this.hidden = true;
        this.listEl.innerHTML = '';
        this._updateFocusTrap();
        return;
      }
      var self = this;
      this.listEl.innerHTML = items.map(this.itemHTML.bind(this)).join('');
      this.listEl.querySelectorAll('[data-swymcs-sfl-move]').forEach(function (btn) {
        btn.addEventListener('click', self._onMoveToCart.bind(self));
      });
      this.listEl.querySelectorAll('[data-swymcs-sfl-remove]').forEach(function (btn) {
        btn.addEventListener('click', self._onRemove.bind(self));
      });
      this.hidden = false;
      this._updateFocusTrap();
    }

    // Keeps keyboard focus trapped inside whichever drawer panel (empty state vs SFL list) is currently visible.
    _updateFocusTrap() {
      var cartDrawer = document.querySelector('cart-drawer.active');
      if (!cartDrawer) return;
      if (cartDrawer.classList.contains('is-empty') && this.hidden) {
        var emptyInner = cartDrawer.querySelector('.drawer__inner-empty');
        if (emptyInner && typeof trapFocus === 'function') trapFocus(emptyInner, emptyInner.querySelector('a'));
        return;
      }

      var drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner && typeof trapFocus === 'function') {
        trapFocus(drawerInner, drawerInner);
      }
    }

    // Builds the markup for a single SFL item, including its price display and action buttons.
    itemHTML(item) {
      var image = item.iu && typeof item.iu !== 'string' ? item.iu.src || null : item.iu;

      var variant = item.productData && Array.isArray(item.productData.variants)
        ? item.productData.variants.filter(function (v) { return String(v.id) === String(item.epi); })[0]
        : null;
      var currentPrice = variant ? variant.price : (item.productData ? item.productData.price : null);
      var compareAtPrice = variant ? variant.compare_at_price : (item.productData ? item.productData.compare_at_price : null);
      if (currentPrice == null) {
        currentPrice = item.pr;
        compareAtPrice = null;
      } else if (!(compareAtPrice > currentPrice)) {
        compareAtPrice = null;
      }

      var priceHtml;
      if (compareAtPrice != null) {
        priceHtml =
          '<div class="cart-item__discounted-prices mt-1">' +
          '<span class="visually-hidden">' + SwymcsSflContainer.escapeHtml(SwymcsSflContainer.getSwymString('regularPriceLabel', 'Regular price')) + '</span>' +
          '<s class="cart-item__old-price product-option">' + SwymcsSflContainer.formatPrice(compareAtPrice) + '</s> ' +
          '<span class="visually-hidden">' + SwymcsSflContainer.escapeHtml(SwymcsSflContainer.getSwymString('salePriceLabel', 'Sale price')) + '</span>' +
          '<strong class="cart-item__final-price product-option">' + SwymcsSflContainer.formatPrice(currentPrice) + '</strong>' +
          '</div>';
      } else {
        priceHtml = '<div class="product-option mt-1">' + SwymcsSflContainer.formatPrice(currentPrice) + '</div>';
      }
      var removeLabel = SwymcsSflContainer.getSwymString('removeSflItemCta', 'Remove from Saved for Later') + ': ' + (item.dt || '');
      var moveLabel = SwymcsSflContainer.getSwymString('moveToCartCta', 'Move to Cart');
      var itemUrl = item.du || '#';
      return (
        '<div class="swymcs-sfl-item flex gap-x-6 items-stretch" data-epi="' + SwymcsSflContainer.escapeHtml(item.epi) + '" data-empi="' + SwymcsSflContainer.escapeHtml(item.empi) + '" data-du="' + SwymcsSflContainer.escapeHtml(item.du) + '" data-qty="' + SwymcsSflContainer.escapeHtml(item.qty || 1) + '">' +
        '<button type="button" class="swymcs-sfl-item__remove" data-swymcs-sfl-remove aria-label="' + SwymcsSflContainer.escapeHtml(removeLabel) + '"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" class="icon icon-remove"><path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/><path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"/></svg></button>' +
        '<div class="cart-item__media swymcs-sfl-item__media">' +
        (image ? '<img src="' + SwymcsSflContainer.escapeHtml(image) + '" alt="" width="90" height="113" loading="lazy" onerror="this.remove()">' : '') +
        '</div>' +
        '<div class="flex-1 swymcs-sfl-item__info">' +
        '<a href="' + SwymcsSflContainer.escapeHtml(itemUrl) + '" class="cart-item__name break">' + SwymcsSflContainer.escapeHtml(item.dt) + '</a>' +
        (item.vi ? '<p class="product-option mt-1">' + SwymcsSflContainer.escapeHtml(item.vi.split('/').join(' / ')) + '</p>' : '') +
        priceHtml +
        '<div class="mt-6">' +
        '<button type="button" class="btn btn-outline swymcs-sfl-item__move" data-swymcs-sfl-move>' + SwymcsSflContainer.escapeHtml(moveLabel) + '</button>' +
        '</div>' +
        '</div>' +
        '</div>'
      );
    }

    // Adds the item to the cart via Shopify's own cart API, then removes it from SFL and refreshes both.
    _onMoveToCart(event) {
      var self = this;
      var button = event.currentTarget;
      var el = button.closest('.swymcs-sfl-item');
      var product = { epi: el.dataset.epi, empi: el.dataset.empi, du: el.dataset.du };
      var qty = Number(el.dataset.qty) || 1;
      button.disabled = true;

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(product.epi), quantity: qty }] }),
      })
        .then(function (response) { return response.json(); })
        .then(function (cartAddResponse) {
          if (cartAddResponse.status) {
            throw new Error(cartAddResponse.description || 'Could not add item to cart');
          }
          var context = window.SwymStorefrontLayoutContext;
          var sflApi = SwymcsSflContainer.getSflApi();
          var removed = sflApi && typeof sflApi.removeProductFromSFL === 'function'
            ? sflApi.removeProductFromSFL(product, context && context.sflListId)
            : Promise.resolve();
          return removed
            .then(function (res) {
              // A resolved promise doesn't mean the item was deleted - always check itemsfailed.
              if (res && Array.isArray(res.itemsfailed) && res.itemsfailed.length) {
                console.error('[swymcs-sfl-container] removeProductFromSFL reported failure after moveToCart', res.itemsfailed[0]);
              }
            })
            .catch(function (err) {
              console.error('[swymcs-sfl-container] removeProductFromSFL failed after moveToCart', err);
            });
        })
        .then(function () {
          SwymcsSflContainer.updateSflCacheTrusted();
          var cartRefresh = typeof window._SwymAJAXCart === 'function' ? window._SwymAJAXCart() : null;
          if (cartRefresh && typeof cartRefresh.then === 'function') {
            cartRefresh.then(function () { self._updateFocusTrap(); });
          }
        })
        .catch(function (err) {
          console.error('[swymcs-sfl-container] move to cart failed', err);
          button.disabled = false;
        });
    }

    // Removes the item from the SFL list via Swym's API, then refreshes the cached list.
    _onRemove(event) {
      var button = event.currentTarget;
      var el = button.closest('.swymcs-sfl-item');
      var product = { epi: el.dataset.epi, empi: el.dataset.empi, du: el.dataset.du };
      button.disabled = true;

      var sflApi = SwymcsSflContainer.getSflApi();
      if (!sflApi || typeof sflApi.removeProductFromSFL !== 'function') {
        console.error('[swymcs-sfl-container] Swym SFL API unavailable, cannot remove');
        button.disabled = false;
        return;
      }

      var context = window.SwymStorefrontLayoutContext;
      sflApi.removeProductFromSFL(product, context && context.sflListId)
        .then(function (res) {
          // A resolved promise doesn't mean the item was deleted - always check itemsfailed.
          if (res && Array.isArray(res.itemsfailed) && res.itemsfailed.length) {
            console.error('[swymcs-sfl-container] remove reported failure', res.itemsfailed[0]);
          }
        })
        .catch(function (err) {
          console.error('[swymcs-sfl-container] remove failed', err);
          button.disabled = false;
        })
        .finally(SwymcsSflContainer.updateSflCacheTrusted);
    }

    static formatPrice(price) {
      try {
        if (typeof formatMoney === 'function' && window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) {
          return formatMoney(price * 100, window.theme.settings.money_with_currency_format);
        }
      } catch (err) {
        console.error('[swymcs-sfl-container] price format failed', err);
      }
      return price;
    }

    static getSwymString(key, fallback) {
      var strings = window.SwymStorefrontLayoutContext && window.SwymStorefrontLayoutContext.Strings;
      return (strings && strings[key]) || fallback;
    }

    static getSflApi() {
      return window.SwymStorefrontLayoutAPI && window.SwymStorefrontLayoutAPI.SwymSFLAsyncApis;
    }

    static escapeHtml(value) {
      var div = document.createElement('div');
      div.textContent = value == null ? '' : value;
      return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    // Triggers a fresh SFL fetch; the result arrives later via the SavedForLaterFetched event, not this call.
    // Returns whether a fetch was actually started, since the caller may need to know no event is coming.
    static updateSflCache() {
      var sflApi = SwymcsSflContainer.getSflApi();
      if (!sflApi || typeof sflApi.updateSflList !== 'function') return false;
      sflApi.updateSflList(true);
      return true;
    }

    // acq-wishlist-snippets.liquid drops list-shrinking writes to sflListItems unless this flag is set. If no
    // fetch actually starts, no SavedForLaterFetched event is coming to clear it, so clear it right away.
    static updateSflCacheTrusted() {
      window._acqTrustedListWrite = true;
      if (!SwymcsSflContainer.updateSflCache()) {
        window._acqTrustedListWrite = false;
      }
    }
  }

  customElements.define('swymcs-sfl-container', SwymcsSflContainer);
})();
